import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Container, Icon } from '@/components/ui';
import { ROUTES } from '@/constants';
import { PRIMARY_NAV } from '@/data';
import {
  useActiveSection,
  useDisclosure,
  useLockBodyScroll,
  useOnKeyDown,
  useScrolled,
} from '@/hooks';
import { cn } from '@/utils';

import { Logo } from '../Logo';

import styles from './Header.module.css';

/** Section ids the nav tracks, derived from the nav itself. */
const TRACKED = PRIMARY_NAV.map((item) => item.to.replace('/#', ''));

export function Header() {
  const { pathname, hash } = useLocation();
  const isScrolled = useScrolled();
  const { isOpen, close, toggle } = useDisclosure();

  // TRACKED is a module constant, so its identity is already stable.
  const activeSection = useActiveSection(TRACKED);
  const isHome = pathname === ROUTES.home;

  useLockBodyScroll(isOpen);
  useOnKeyDown('Escape', close, isOpen);

  // Following an anchor should always leave the drawer closed. `close` is
  // stable, so this runs on navigation only.
  useEffect(() => {
    close();
  }, [pathname, hash, close]);

  const isActive = (to: string) => isHome && activeSection === to.replace('/#', '');

  return (
    <>
      <a className={styles.skipLink} href="#main-content">
        Skip to content
      </a>

      <header className={cn(styles.header, isScrolled && styles.scrolled)}>
        <Container className={styles.inner}>
          <Logo inverted />

          <nav className={styles.nav} aria-label="Primary">
            <ul className={styles.navList} role="list">
              {PRIMARY_NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={cn(styles.navLink, isActive(item.to) && styles.navLinkActive)}
                    aria-current={isActive(item.to) ? 'true' : undefined}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Parked — uncomment to bring the header call to action back. */}
          {/*
          <div className={styles.actions}>
            <ButtonLink to={anchor(SECTIONS.contact)} size="sm">
              Talk to us
            </ButtonLink>
          </div>
          */}

          {/* Icon-only control: a plain button, so the fixed square size is
              not fighting the Button primitive's padding. */}
          <button
            type="button"
            className={styles.menuButton}
            onClick={toggle}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label="Open menu"
          >
            <Icon name="menu" size={22} />
          </button>
        </Container>
      </header>

      {isOpen ? (
        <>
          <button
            type="button"
            tabIndex={-1}
            className={styles.overlay}
            onClick={close}
            aria-label="Close menu"
          />
          <div
            className={styles.drawer}
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
          >
            <div className={styles.drawerHeader}>
              <Logo inverted />
              <button
                type="button"
                className={styles.menuButton}
                onClick={close}
                aria-label="Close menu"
              >
                <Icon name="close" size={22} />
              </button>
            </div>

            <nav aria-label="Mobile">
              <ul className={styles.drawerNav} role="list">
                {PRIMARY_NAV.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={cn(
                        styles.drawerLink,
                        isActive(item.to) && styles.drawerLinkActive,
                      )}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/*
              No action button: "Start a pilot" is a nav entry above now, and a
              button repeating it is the only thing the drawer had left. To
              restore either, put `ButtonLink`, `SECTIONS` and `anchor` back in
              the imports at the top of this file.

            <div className={styles.drawerActions}>
              <ButtonLink to={anchor(SECTIONS.contact)} fullWidth>
                Talk to us
              </ButtonLink>
              <ButtonLink to={anchor(SECTIONS.pilot)} fullWidth>
                Start a pilot
              </ButtonLink>
            </div>
            */}
          </div>
        </>
      ) : null}
    </>
  );
}
